import { Input, Stack, Text, XStack, YStack } from "tamagui";
import type { LastOrderGasItem } from "@screen/Home/pages/GasShop/products";
import { floorIntToLabel, kiosk, PRESET_USAGE_LOCATIONS, USAGE_ICON } from "../kioskTheme";
import KioskIcon from "../KioskIcon";
import { BigOption, Question } from "./KioskParts";

export const FLOORS = ["B1", "1F", "2F", "3F", "4F", "5F", "6F+"];

/** step 0：使用位置（預設 + 使用者過去用過的自訂選項 + 「跟上次一樣」捷徑 + 其他自訂輸入）。 */
export const StepUse = ({
  lastItem,
  lastLabel,
  customUsages,
  useSel,
  useOther,
  onUseSame,
  onPickUse,
  onChangeOther,
}: {
  lastItem: LastOrderGasItem | null;
  lastLabel: string;
  customUsages: string[]; // 使用者過去用過、非預設的使用位置（GET customer_delivery 取得）
  useSel: string | null;
  useOther: string;
  onUseSame: () => void;
  onPickUse: (v: string) => void;
  onChangeOther: (v: string) => void;
}) => (
  <YStack className="kiosk-stepin">
    {lastItem?.install_location && (
      <YStack
        className="kiosk-grad-soft"
        borderWidth={1}
        borderColor={kiosk.softCardBorder}
        borderRadius={18}
        p={16}
        mb={20}
        cursor="pointer"
        onPress={onUseSame}
        pressStyle={{ scale: 0.99 }}>
        <Text fontSize={11} fontWeight="800" color={kiosk.blueD} mb={6} letterSpacing={0.5}>
          ↻ 跟上次一樣
        </Text>
        <Text fontSize={13.5} fontWeight="600" color="#2a3550" lineHeight={20}>
          {lastLabel}
        </Text>
        <XStack className="kiosk-grad" borderRadius={12} py={11} mt={12} ai="center" jc="center">
          <Text color="#fff" fontSize={14} fontWeight="800">
            一鍵套用 ›
          </Text>
        </XStack>
      </YStack>
    )}

    <Question q="安裝在哪裡？" sub="選擇這桶瓦斯的使用位置" />
    <XStack flexWrap="wrap" gap={13}>
      {[...PRESET_USAGE_LOCATIONS, ...customUsages, "其他"].map((u) => (
        <Stack key={u} style={{ width: "calc(50% - 7px)" } as any}>
          <BigOption icon={u === "其他" ? "dots" : ((USAGE_ICON[u] as any) || "flame")} label={u} selected={useSel === u} onPress={() => onPickUse(u)} />
        </Stack>
      ))}
    </XStack>
    {useSel === "其他" && (
      <YStack mt={13}>
        <Text fontSize={13} color="#46506a" fontWeight="700" mb={8}>
          請輸入使用位置
        </Text>
        <Input
          value={useOther}
          onChangeText={(t: string) => onChangeOther(t)}
          maxLength={12}
          placeholder="例如：烤箱、營業用爐具"
          borderWidth={2}
          borderColor={kiosk.blue}
          borderRadius={13}
          backgroundColor="#fff"
          color={kiosk.ink}
          fontSize={16}
          h={52}
        />
        <Text fontSize={11} color="#9aa3b2" mt={8}>
          填寫後按「下一步」繼續
        </Text>
      </YStack>
    )}
  </YStack>
);

/** step 1：是否有電梯。 */
export const StepLift = ({ lift, onPick }: { lift: boolean; onPick: (v: boolean) => void }) => (
  <YStack className="kiosk-stepin">
    <Question q="有電梯嗎？" sub="影響搬運方式與樓層費" />
    <XStack gap={13}>
      <Stack flex={1}>
        <BigOption icon="elevator" label="有電梯" selected={lift} onPress={() => onPick(true)} />
      </Stack>
      <Stack flex={1}>
        <BigOption icon="stairs" label="無電梯" selected={!lift} onPress={() => onPick(false)} />
      </Stack>
    </XStack>
  </YStack>
);

/** step 1：樓層（含 6F+ 自訂、樓層費提示）。先問樓層，1F 於外層跳過電梯步驟。 */
export const StepFloor = ({
  floorSel,
  floorOther,
  onPickFloor,
  onChangeOther,
}: {
  floorSel: string;
  floorOther: string;
  onPickFloor: (v: string) => void;
  onChangeOther: (v: string) => void;
}) => (
  <YStack className="kiosk-stepin">
    <Question q="在幾樓？" sub="點一下選擇樓層" />
    <XStack flexWrap="wrap" gap={11}>
      {FLOORS.map((f) => (
        <Stack key={f} style={{ width: "calc(25% - 9px)" } as any}>
          <BigOption label={f} selected={floorSel === f} onPress={() => onPickFloor(f)} />
        </Stack>
      ))}
    </XStack>
    {floorSel === "6F+" && (
      <YStack mt={13}>
        <Text fontSize={13} color="#46506a" fontWeight="700" mb={8}>
          請輸入樓層
        </Text>
        <Input
          value={floorOther}
          onChangeText={(t: string) => onChangeOther(t.toUpperCase())}
          maxLength={6}
          placeholder="例如：12F、B2"
          borderWidth={2}
          borderColor={kiosk.blue}
          borderRadius={13}
          backgroundColor="#fff"
          color={kiosk.ink}
          fontSize={16}
          h={52}
        />
      </YStack>
    )}
    <YStack mt={16} backgroundColor={kiosk.amberBg} borderWidth={1} borderColor={kiosk.amberBorder} borderRadius={12} p={14}>
      <Text fontSize={12.5} color={kiosk.amber} lineHeight={18}>
        樓層越高{" "}
        <Text color={kiosk.amberStrong} fontWeight="700">
          可能加收越多
        </Text>{" "}
        樓層費，1F 通常免收；實際費用以瓦斯行確認為準，將於確認訂單頁顯示。
      </Text>
    </YStack>
  </YStack>
);

/** step 3：數量 + 備註 + 摘要。 */
export const StepQty = ({
  qty,
  note,
  productName,
  usageLocation,
  floorInt,
  floorSel,
  lift,
  onDec,
  onInc,
  onChangeNote,
  onJump,
}: {
  qty: number;
  note: string;
  productName: string;
  usageLocation: string;
  floorInt: number | null;
  floorSel: string;
  lift: boolean;
  onDec: () => void;
  onInc: () => void;
  onChangeNote: (v: string) => void;
  onJump: (step: number) => void; // 點摘要列跳回對應步驟修改
}) => (
  <YStack className="kiosk-stepin">
    <Question q="要幾桶？" sub="確認後加入購物車" />
    <XStack ai="center" jc="center" gap={26} mt={20}>
      <Stack onPress={onDec} cursor="pointer" width={62} height={62} borderRadius={31} borderWidth={2} borderColor={kiosk.line} ai="center" jc="center" pressStyle={{ scale: 0.9 }}>
        <Text fontSize={30} color={kiosk.blue}>
          −
        </Text>
      </Stack>
      <Text fontSize={42} fontWeight="800" minWidth={60} textAlign="center" color={kiosk.ink}>
        {qty}
      </Text>
      <Stack onPress={onInc} cursor="pointer" width={62} height={62} borderRadius={31} borderWidth={2} borderColor={kiosk.line} ai="center" jc="center" pressStyle={{ scale: 0.9 }}>
        <Text fontSize={30} color={kiosk.blue}>
          ＋
        </Text>
      </Stack>
    </XStack>

    <YStack mt={22}>
      <Text fontSize={13} color="#46506a" fontWeight="700" mb={8}>
        安裝位置備註（選填）
      </Text>
      <Input
        value={note}
        onChangeText={(t: string) => onChangeNote(t)}
        maxLength={30}
        placeholder="例如：廚房、後巷、大門進去左側"
        borderWidth={1.5}
        borderColor={kiosk.line}
        borderRadius={13}
        backgroundColor="#fff"
        color={kiosk.ink}
        fontSize={15}
        h={50}
      />
    </YStack>

    <YStack mt={24} backgroundColor="#f4f6fb" borderRadius={16} p={16} gap={2}>
      <RecapRow k="品項" v={productName} />
      <RecapRow k="使用位置" v={usageLocation || "—"} onEdit={() => onJump(0)} />
      <RecapRow k="樓層" v={floorInt !== null ? floorIntToLabel(floorInt) : floorSel} onEdit={() => onJump(1)} last={floorInt === 1 && !note.trim()} />
      {floorInt !== 1 && <RecapRow k="電梯" v={lift ? "有電梯" : "無電梯"} onEdit={() => onJump(2)} last={!note.trim()} />}
      {note.trim() && <RecapRow k="備註" v={note.trim()} last />}
    </YStack>
  </YStack>
);

const RecapRow = ({ k, v, last, onEdit }: { k: string; v: string; last?: boolean; onEdit?: () => void }) => (
  <XStack
    jc="space-between"
    ai="center"
    py={7}
    borderBottomWidth={last ? 0 : 1}
    borderColor="#edf0f5"
    gap={10}
    cursor={onEdit ? "pointer" : undefined}
    onPress={onEdit}
    pressStyle={onEdit ? { opacity: 0.6 } : undefined}>
    <Text fontSize={14} color="#6b7280">
      {k}
    </Text>
    <XStack ai="center" gap={5} flexShrink={1}>
      <Text fontSize={14} color={onEdit ? kiosk.blue : kiosk.ink} fontWeight="700" textAlign="right">
        {v}
      </Text>
      {onEdit && <KioskIcon name="chevron" size={15} color={kiosk.blue} strokeWidth={2.4} />}
    </XStack>
  </XStack>
);
